import { useEffect, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';

const HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Periodically writes last_active_at to the users table while the tab is visible.
 * Fires once immediately on mount, then every 5 minutes while the tab is active.
 * Stops automatically when the tab is hidden to avoid writes from idle background tabs.
 */
export function useActivityHeartbeat() {
  const { user } = useAuth();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!user?.id) return;

    const ping = async () => {
      if (document.visibilityState !== 'visible') return;
      await supabase
        .from('users')
        .update({ last_active_at: new Date().toISOString() })
        .eq('id', user.id);
    };

    const startInterval = () => {
      if (intervalRef.current) return;
      ping(); // immediate ping when tab becomes visible
      intervalRef.current = setInterval(ping, HEARTBEAT_INTERVAL_MS);
    };

    const stopInterval = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        startInterval();
      } else {
        stopInterval();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Start if tab is already visible
    if (document.visibilityState === 'visible') {
      startInterval();
    }

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      stopInterval();
    };
  }, [user?.id]);
}
